import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Logo } from '../components/common/Logo';
import { AudioSettingsPanel } from '../components/settings/AudioSettingsPanel';
import { CompanionDownloadPanel } from '../components/settings/CompanionDownloadPanel';
import { HeadsetPairingPanel } from '../components/settings/HeadsetPairingPanel';
import { ProfileSettingsPanel } from '../components/settings/ProfileSettingsPanel';
import { VideoSettingsPanel } from '../components/settings/VideoSettingsPanel';
import { useAudioLevel } from '../hooks/useAudioLevel';
import { useCameraStream } from '../hooks/useCameraStream';
import { useMediaDevices } from '../hooks/useMediaDevices';

/**
 * Settings page for device configuration.
 */
export default function SettingsPage() {
  const navigate = useNavigate();
  const {
    microphones,
    cameras,
    speakers,
    selectedMicrophone,
    selectedCamera,
    selectedSpeaker,
    selectMicrophone,
    selectCamera,
    selectSpeaker,
  } = useMediaDevices();

  const cameraStream = useCameraStream(selectedCamera);
  const audioLevel = useAudioLevel(selectedMicrophone);

  return (
    <div className="min-h-screen">
      <SettingsHeader onBack={() => navigate(-1)} />
      <SettingsContent
        microphones={microphones}
        speakers={speakers}
        cameras={cameras}
        selectedMicrophone={selectedMicrophone}
        selectedSpeaker={selectedSpeaker}
        selectedCamera={selectedCamera}
        onSelectMicrophone={selectMicrophone}
        onSelectSpeaker={selectSpeaker}
        onSelectCamera={selectCamera}
        audioLevel={audioLevel}
        cameraStream={cameraStream}
      />
    </div>
  );
}

interface SettingsHeaderProps {
  onBack: () => void;
}

/**
 * Header for the settings page.
 */
function SettingsHeader({ onBack }: SettingsHeaderProps) {
  return (
    <header className="sticky top-0 z-20 glass border-b border-white/10">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={onBack}>
            Back
          </Button>
          <Logo size="sm" />
        </div>
        <h1 className="text-xl font-semibold">Settings</h1>
      </div>
    </header>
  );
}

interface SettingsContentProps {
  microphones: { deviceId: string; label: string }[];
  speakers: { deviceId: string; label: string }[];
  cameras: { deviceId: string; label: string }[];
  selectedMicrophone: string;
  selectedSpeaker: string;
  selectedCamera: string;
  onSelectMicrophone: (id: string) => void;
  onSelectSpeaker: (id: string) => void;
  onSelectCamera: (id: string) => void;
  audioLevel: number;
  cameraStream: MediaStream | null;
}

/**
 * Main settings content area.
 */
function SettingsContent({
  microphones,
  speakers,
  cameras,
  selectedMicrophone,
  selectedSpeaker,
  selectedCamera,
  onSelectMicrophone,
  onSelectSpeaker,
  onSelectCamera,
  audioLevel,
  cameraStream,
}: SettingsContentProps) {
  return (
    <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <ProfileSettingsPanel />
        <CompanionDownloadPanel />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <AudioSettingsPanel
          microphones={microphones}
          speakers={speakers}
          selectedMicrophone={selectedMicrophone}
          selectedSpeaker={selectedSpeaker}
          onSelectMicrophone={onSelectMicrophone}
          onSelectSpeaker={onSelectSpeaker}
          audioLevel={audioLevel}
        />
        <VideoSettingsPanel
          cameras={cameras}
          selectedCamera={selectedCamera}
          onSelectCamera={onSelectCamera}
          cameraStream={cameraStream}
        />
      </div>

      <HeadsetPairingPanel />
    </main>
  );
}
